import React, { useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'

import { AppDispatch, GlobalState } from '../../../store'
import {
  disableInputs,
  DraftActions,
  draftInputs,
  DraftSources,
  removeInputs,
} from '../../../redux/actions/inputsActions'
import { pluralize } from '../../../utils'

interface CommonActionsProps {
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
}

const DeleteDialog = ({ setSelected }: CommonActionsProps) => {
  const [confirmTextValue, setConfirmTextValue] = useState('')

  const {
    draft: { inputs, action, source },
  } = useSelector(
    ({ inputsReducer }: GlobalState) => ({
      draft: inputsReducer.draft,
    }),
    shallowEqual,
  )
  const dispatch = useDispatch<AppDispatch>()
  if (!inputs.length) return null

  const onCancelClick = () => {
    setConfirmTextValue('')
    dispatch(draftInputs({ inputs: [] }))
  }

  const handleOk = () => {
    setConfirmTextValue('')

    dispatch(draftInputs({ inputs: [] }))
    if (action === DraftActions.delete) {
      dispatch(removeInputs(inputs.map(({ id }) => id)))
    }
    if (action === DraftActions.disable) {
      dispatch(disableInputs(inputs.map(({ id }) => id)))
      setSelected([])
    }
  }
  const isInputInUse = inputs.some((input) => !!input.numOutputs)
  const confirmText = (inputs.length === 1 ? inputs[0].name : `${action} inputs`).toLowerCase()
  const isConfirmRequired = source === DraftSources.batch && action === DraftActions.delete

  return (
    <Dialog
      open={inputs.length > 0 && (action === DraftActions.delete || action === DraftActions.disable)}
      disableRestoreFocus
      onClose={(_event, reason) => {
        if (reason === 'escapeKeyDown') {
          onCancelClick()
        }
      }}
      data-test-id="confirm-dialog"
    >
      <DialogContent>
        <DialogContentText gutterBottom component="div">
          {!isInputInUse &&
            `Are you sure you want to ${action} ${
              inputs.length > 1 ? pluralize(inputs.length, 'input') : inputs[0].name
            }?`}

          {isInputInUse && (
            <>
              {inputs.length > 1
                ? `Some inputs are in use - are you sure you want to ${action} them?`
                : `${inputs[0].name} is in use - are you sure you want to ${action} it?`}
              {inputs.length > 1 && (
                <ul>
                  {inputs.map((item) => (
                    <li key={item.id}>
                      {item.name}
                      {!!item.numOutputs && ` (in use by ${pluralize(item.numOutputs, 'output')})`}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </DialogContentText>
        {isConfirmRequired && (
          <>
            <DialogContentText gutterBottom>
              To confirm, type{' '}
              <span style={{ fontStyle: 'italic', fontWeight: 'bold' }} data-test-id="confirm-text-value">
                {confirmText}
              </span>{' '}
              below
            </DialogContentText>
            <TextField
              data-test-id="confirm-text-field"
              autoFocus
              fullWidth
              placeholder={confirmText}
              value={confirmTextValue}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && confirmTextValue.toLowerCase() === confirmText) {
                  handleOk()
                }
              }}
              onChange={(e) => setConfirmTextValue(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onCancelClick} data-test-id="cancel-btn">
          Cancel
        </Button>
        <Button
          variant="contained"
          color={action === DraftActions.delete ? 'error' : 'primary'}
          onClick={handleOk}
          data-test-id="ok-btn"
          disabled={isConfirmRequired ? confirmTextValue.toLowerCase() !== confirmText : false}
        >
          {action}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DeleteDialog
